import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import api from '../config/api';
import { toast } from '../hooks/use-toast';

/* Department picker (Oct 2026): a dropdown of the departments already in use
   (the HR employee sheet's, plus ones people have typed), and "Not in the list"
   to type a new one. Settings → Usage groups people by it. By default it's the
   HR sheet's; anyone can change theirs here (the sheet itself isn't edited) and
   go back to the sheet's. `state` is GET /auth/me/department. */

const OTHER = '__other__';

const DepartmentField = ({ state, onSaved, saveLabel = 'Save', autoFocus = false }) => {
  const options = state?.options || [];
  const current = state?.department || '';
  const initialChoice = () => (current ? (options.includes(current) ? current : OTHER) : (options.length ? '' : OTHER));
  const [choice, setChoice] = useState(initialChoice);
  const [typed, setTyped] = useState(current && !options.includes(current) ? current : '');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setChoice(initialChoice());
    setTyped(current && !options.includes(current) ? current : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, options.join('|')]);

  const value = (choice === OTHER ? typed : choice).trim();
  const sheetDept = state?.sheet_department || '';
  const save = async (body = { department: value }) => {
    if ((!body.use_sheet && !value) || saving) return;
    setSaving(true);
    try {
      const res = await api.put('/auth/me/department', body);
      toast({ title: 'Department saved', description: res.data.department });
      onSaved?.(res.data);
    } catch (e) {
      toast({ title: 'Not saved', description: e.response?.data?.detail || 'Please try again', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="department-field">
      {options.length > 0 && (
        <select value={choice} onChange={(e) => setChoice(e.target.value)} autoFocus={autoFocus && choice !== OTHER}
          aria-label="Department" data-testid="department-select"
          className="h-10 min-w-[220px] flex-1 rounded-md border border-input bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring">
          <option value="" disabled>Select your department</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
          <option value={OTHER}>Not in the list — type it…</option>
        </select>
      )}
      {choice === OTHER && (
        <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Type your department" maxLength={80}
          autoFocus={autoFocus || options.length > 0} aria-label="Type your department" className="min-w-[200px] flex-1"
          onKeyDown={(e) => { if (e.key === 'Enter') save(); }} data-testid="department-input" />
      )}
      <Button onClick={() => save()} disabled={!value || value === current || saving} data-testid="department-save"
        className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700">
        {saving ? 'Saving…' : saveLabel}
      </Button>
      {state?.source === 'sheet' && (
        <p className="w-full text-xs text-gray-400" data-testid="department-from-sheet">From the HR employee sheet — change it here if it’s not right for you.</p>
      )}
      {state?.source === 'self' && sheetDept && sheetDept !== current && (
        <p className="w-full text-xs text-gray-400" data-testid="department-sheet-differs">
          The HR employee sheet says {sheetDept}.{' '}
          <button type="button" className="font-medium text-orange-600 hover:text-orange-700" disabled={saving}
            onClick={() => save({ use_sheet: true })} data-testid="department-use-sheet">
            Use the HR sheet’s
          </button>
        </p>
      )}
    </div>
  );
};

export default DepartmentField;
